import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Box,
  Paper,
  Typography,
  Container,
  LinearProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { ChevronLeft, ChevronRight, CheckCircle, Pending, ArrowBack, Notifications } from '@mui/icons-material';
import BasicDetailsForm from './forms/BasicDetailsForm';
import ThirdPartyRiskForm from './forms/ThirdPartyRiskForm';
// import ApprovalDueDiligenceForm from './forms/ApprovalDueDiligenceForm';
import CommercialDueDiligenceForm from './forms/CommercialDueDiligenceForm';
import BidsForm from './forms/BidsForm';
import SupplierReviewForm from './forms/SupplierReviewForm';
import RejectionBanner from './RejectionBanner';
import { useNotification } from './NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { validateAllForms, FormValidationErrors, hasValidationErrors, getValidationSummary, isSelfApprovingManager, SELF_APPROVAL_MESSAGE } from '../utils/formValidation';

type WorkflowStatus = 'DRAFT' | 'PENDING_MANAGER_APPROVAL' | 'PENDING_LEGAL' | 'PENDING_AP' | 'COMPLETED' | 'REJECTED';

interface FormData {
  workflow: {
    status: WorkflowStatus;
    createdByUserId?: string;
    submittedDate?: string;
    approverApprovalDate?: string;
    legalApprovalDate?: string;
    apApprovalDate?: string;
    requiresLegalReview: boolean;
    rejectionReason?: string;
    vendorAddedToRamp?: boolean;
    w9FilePath?: string;
    w9FileName?: string;
  };
  basicDetails: {
    estimatedContractAmount: string;
    needsContractorAgreement: boolean;
    requestorName: string;
    requestorCode?: string;
    requestorEmail?: string;
    requestorManager: string;
    requestorManagerCode?: string;
    requestorManagerEmail?: string;
    companyName: string;
    streetAddress: string;
    addressLine2: string;
    city: string;
    zipCode: string;
    state: string;
    country: string;
    phone: string;
    contactPerson: string;
    contactEmail: string;
    vendorWebsite: string;
    howLearnedAbout: string;
    goodsServicesDescription: string;
  };
  thirdPartyRisk: {
    ofacSanctions: string;
    ofacSanctionsDetails: string;
    angelRelationships: string;
    angelRelationshipsDetails: string;
    personalInfoAccess: string;
    personalInfoDetails: string;
    specialCircumstances: string;
    specialCircumstancesDetails: string;
    paymentTerms?: string;
    projectDeliverables?: string;
    freeFromControl?: string;
    workOutsideOfNormalBusiness?: string;
    independentlyEstablishedBusiness?: string;
    effectiveDate?: string;
    expirationDate?: string;
  };
  approvalDueDiligence: {
    approverName: string;
    legalClearance: string;
    notes: string;
  };
  commercialDueDiligence: {
    reputationalConcerns: string;
    reputationalConcernsDetails: string;
    searchTermsTools: string;
    experienceCredentials: string;
  };
  bids: {
    competitiveBidsObtained: string;
    bid1Description: string;
    bid1Total: string;
    bid2Description: string;
    bid2Total: string;
    bid3Description: string;
    bid3Total: string;
    selectionReason: string;
  };
}

const MultiStepForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { identity, user, isAdmin, isSuperAdmin } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [approvalStage, setApprovalStage] = useState<'manager' | 'legal' | 'ap'>('manager');
  const [validationErrors, setValidationErrors] = useState<FormValidationErrors>({
    basicDetails: [],
    thirdPartyRisk: [],
    commercialDueDiligence: [],
    bids: [],
  });

  const [formData, setFormData] = useState<FormData>({
    workflow: {
      status: 'DRAFT',
      requiresLegalReview: false,
    },
    basicDetails: {
      estimatedContractAmount: '',
      needsContractorAgreement: false,
      requestorName: '',
      requestorCode: '',
      requestorEmail: '',
      requestorManager: '',
      requestorManagerCode: '',
      requestorManagerEmail: '',
      companyName: '',
      streetAddress: '',
      addressLine2: '',
      city: '',
      zipCode: '',
      state: '',
      country: '',
      phone: '',
      contactPerson: '',
      contactEmail: '',
      vendorWebsite: '',
      howLearnedAbout: '',
      goodsServicesDescription: '',
    },
    thirdPartyRisk: {
      ofacSanctions: '',
      ofacSanctionsDetails: '',
      angelRelationships: '',
      angelRelationshipsDetails: '',
      personalInfoAccess: '',
      personalInfoDetails: '',
      specialCircumstances: '',
      specialCircumstancesDetails: '',
    },
    approvalDueDiligence: {
      approverName: '',
      legalClearance: '',
      notes: '',
    },
    commercialDueDiligence: {
      reputationalConcerns: '',
      reputationalConcernsDetails: '',
      searchTermsTools: '',
      experienceCredentials: '',
    },
    bids: {
      competitiveBidsObtained: '',
      bid1Description: '',
      bid1Total: '',
      bid2Description: '',
      bid2Total: '',
      bid3Description: '',
      bid3Total: '',
      selectionReason: '',
    },
  });

  // Compute dynamic routing based on business rules (reactive to formData changes)
  const route = useMemo(() => {
    const amount = parseFloat(formData.basicDetails.estimatedContractAmount) || 0;
    const needsContract = formData.basicDetails.needsContractorAgreement;
    const requiresAccounting = amount >= 1000;
    
    return {
      requiresAccounting,
      requiresIronclad: needsContract,
    };
  }, [formData.basicDetails.estimatedContractAmount, formData.basicDetails.needsContractorAgreement]);

  // Define active steps based on route (reactive to route changes)
  const activeSteps = useMemo(() => {
    const stepList = ['Basic Details'];
    
    // Risk Profile, Due Diligence and Competitive Bidding are all validated by
    // validateAllForms whenever the request needs accounting review or an
    // Ironclad agreement, so they must be rendered under that same condition.
    // Showing fewer steps than we validate leaves the requestor with blocking
    // errors for forms they were never given a chance to fill out.
    if (route.requiresAccounting || route.requiresIronclad) {
      stepList.push('Risk Profile', 'Due Diligence', 'Competitive Bidding');
    }
    
    // Always add Review as the final step before submission
    stepList.push('Review');
    
    return stepList;
  }, [route.requiresAccounting, route.requiresIronclad]);

  const steps = activeSteps;

  // Load existing data when ID is present
  useEffect(() => {
    const loadFormData = async () => {
      // For new forms, auto-create a minimal DB record to get an ID
      if (id === 'new') {
        try {
          // Get requestor info from employee list
          let requestorInfo = {
            requestorName: '',
            requestorCode: '',
            requestorEmail: '',
          };
          
          if (user?.email) {
            const { loadEmployees } = await import('../utils/employeeData');
            const employees = await loadEmployees();
            const matchingEmployee = employees.find(emp => 
              emp.workEmail.toLowerCase() === user.email.toLowerCase()
            );
            
            if (matchingEmployee) {
              requestorInfo = {
                requestorName: matchingEmployee.employeeName,
                requestorCode: matchingEmployee.employeeCode,
                requestorEmail: matchingEmployee.workEmail,
              };
            } else {
              // If employee not found in list, use user's auth context info
              // Use fullName from auth context if available, otherwise construct from first/last or use email
              const fallbackName = user.fullName || 
                                 (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '') ||
                                 user.email || 
                                 '';
              requestorInfo = {
                requestorName: fallbackName,
                requestorCode: '',
                requestorEmail: user.email || '',
              };
              console.warn(`[MultiStepForm] Employee not found for email ${user.email}, using fallback data: ${fallbackName}`);
            }
          }
          
          // Create minimal form record to get ID
          const minimalPayload = {
            estimated_contract_amount: '',
            needs_contractor_agreement: false,
            requestor_name: requestorInfo.requestorName,
            requestor_code: requestorInfo.requestorCode,
            requestor_email: requestorInfo.requestorEmail,
            requestor_manager: '',
            company_name: '',
            workflow_status: 'DRAFT',
          };
          
          const response = await apiClient.post('/api/vendors', minimalPayload);
          const newId = response.data.id;
          
          console.log(`[MultiStepForm] Auto-created form with ID ${newId} for new request`);
          
          // Redirect to the new ID
          navigate(`/form/${newId}`, { replace: true });
        } catch (error) {
          console.error('Error auto-creating form:', error);
          showNotification('Error creating form', 'error');
          setLoading(false);
        }
        return;
      }
      
      if (id && id !== 'new') {
        try {
          const response = await apiClient.get(`/api/vendors/${id}`);
          const dbData = response.data.data;
          
          // If requestor_name is missing, try to populate it from the current user
          let requestorData = {
            requestorName: dbData.requestor_name || '',
            requestorCode: dbData.requestor_code || '',
            requestorEmail: dbData.requestor_email || '',
          };
          
          if (!dbData.requestor_name && user?.email) {
            // Try to find the user in the employee list
            const { loadEmployees } = await import('../utils/employeeData');
            const employees = await loadEmployees();
            const matchingEmployee = employees.find(emp => 
              emp.workEmail.toLowerCase() === user.email.toLowerCase()
            );
            
            if (matchingEmployee) {
              requestorData = {
                requestorName: matchingEmployee.employeeName,
                requestorCode: matchingEmployee.employeeCode,
                requestorEmail: matchingEmployee.workEmail,
              };
              console.log(`[MultiStepForm] Populated missing requestor from employee list: ${requestorData.requestorName}`);
            } else {
              // Fallback to user auth context info
              const fallbackName = user.fullName || 
                                 (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '') ||
                                 user.email || 
                                 '';
              requestorData = {
                requestorName: fallbackName,
                requestorCode: '',
                requestorEmail: user.email || '',
              };
              console.log(`[MultiStepForm] Populated missing requestor from auth context: ${requestorData.requestorName}`);
            }
          }
          
          // Map database fields to form structure
          setFormData({
              workflow: {
                status: dbData.workflow_status || 'DRAFT',
                createdByUserId: dbData.created_by_user_id || '',
                submittedDate: dbData.submitted_date,
                approverApprovalDate: dbData.approver_approval_date,
                legalApprovalDate: dbData.legal_approval_date,
                apApprovalDate: dbData.ap_approval_date,
                requiresLegalReview: dbData.requires_legal_review || false,
                rejectionReason: dbData.rejection_reason,
                vendorAddedToRamp: dbData.vendor_added_to_ramp || false,
                w9FilePath: dbData.w9_file_path || '',
                w9FileName: dbData.w9_file_path ? dbData.w9_file_path.split('/').pop() : '',
              },
              basicDetails: {
                estimatedContractAmount: dbData.estimated_contract_amount || '',
                needsContractorAgreement: dbData.needs_contractor_agreement || false,
                ...requestorData,
                requestorManager: dbData.requestor_manager || '',
                requestorManagerCode: dbData.requestor_manager_code || '',
                requestorManagerEmail: dbData.requestor_manager_email || '',
                companyName: dbData.company_name || '',
                streetAddress: dbData.street_address || '',
                addressLine2: dbData.address_line_2 || '',
                city: dbData.city || '',
                zipCode: dbData.zip_code || '',
                state: dbData.state || '',
                country: dbData.country || '',
                phone: dbData.phone || '',
                contactPerson: dbData.contact_person || '',
                contactEmail: dbData.contact_email || '',
                vendorWebsite: dbData.vendor_website || '',
                howLearnedAbout: dbData.how_learned_about || '',
                goodsServicesDescription: dbData.goods_services_description || '',
              },
              thirdPartyRisk: {
                ofacSanctions: dbData.ofac_sanctions || '',
                ofacSanctionsDetails: dbData.ofac_sanctions_details || '',
                angelRelationships: dbData.angel_relationships || '',
                angelRelationshipsDetails: dbData.angel_relationships_details || '',
                personalInfoAccess: dbData.personal_info_access || '',
                personalInfoDetails: dbData.personal_info_details || '',
                specialCircumstances: dbData.special_circumstances || '',
                specialCircumstancesDetails: dbData.special_circumstances_details || '',
                paymentTerms: dbData.payment_terms || '',
                projectDeliverables: dbData.project_deliverables || '',
                freeFromControl: dbData.free_from_control || '',
                workOutsideOfNormalBusiness: dbData.work_outside_of_normal_business || '',
                independentlyEstablishedBusiness: dbData.independently_established_business || '',
                effectiveDate: dbData.effective_date ? new Date(dbData.effective_date).toISOString().split('T')[0] : '',
                expirationDate: dbData.expiration_date ? new Date(dbData.expiration_date).toISOString().split('T')[0] : '',
              },
              approvalDueDiligence: {
                approverName: dbData.approver_name || '',
                legalClearance: dbData.legal_clearance || '',
                notes: dbData.approval_notes || '',
              },
              commercialDueDiligence: {
                reputationalConcerns: dbData.reputational_concerns || '',
                reputationalConcernsDetails: dbData.reputational_concerns_details || '',
                searchTermsTools: dbData.search_terms_tools || '',
                experienceCredentials: dbData.experience_credentials || '',
              },
              bids: {
                bid1Description: dbData.bid_1_description || '',
                bid1Total: dbData.bid_1_total || '',
                bid2Description: dbData.bid_2_description || '',
                bid2Total: dbData.bid_2_total || '',
                bid3Description: dbData.bid_3_description || '',
                bid3Total: dbData.bid_3_total || '',
                competitiveBidsObtained: dbData.competitive_bids_obtained || '',
                selectionReason: dbData.selection_reason || '',
              },
          });
        } catch (error) {
          console.error('Error loading form data:', error);
        }
      }
      setLoading(false);
    };

    loadFormData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  // Mock data loader (will be replaced with Snowflake query)
  /*const getMockFormData = (requestId: number): FormData | null => {
    const mockDataMap: { [key: number]: FormData } = {
      1: {
        workflow: {
          status: 'PENDING_MANAGER_APPROVAL',
          submittedDate: '2026-01-15T10:30:00',
          requiresLegalReview: false,
        },
        basicDetails: {
          estimatedContractAmount: '50000',
          requestorName: 'ROSE, TYLER',
          requestorCode: 'A0JW',
          requestorManager: 'WILLARDSON, RAY',
          requestorManagerCode: 'A060',
          companyName: 'Acme Corporation',
          streetAddress: '123 Main St',
          addressLine2: 'Suite 100',
          zipCodeCity: 'Los Angeles, CA 90001',
          country: 'United States',
          phone: '555-1234',
          contactPerson: 'Jane Acme',
          contactEmail: 'jane@acme.com',
          howLearnedAbout: 'Industry referral',
          goodsServicesDescription: 'Marketing services for film production',
          competitiveBidsObtained: 'Yes',
        },
        thirdPartyRisk: {
          angelRelationships: 'No',
          angelRelationshipsDetails: '',
          personalInfoAccess: 'No',
          personalInfoDetails: '',
          specialCircumstances: 'No',
          specialCircumstancesDetails: '',
        },
        approvalDueDiligence: {
          approverName: 'Sarah Johnson',
          legalClearance: 'Yes',
          notes: 'Standard approval process',
        },
        commercialDueDiligence: {
          reputationalConcerns: 'No',
          reputationalConcernsDetails: '',
          researchDate: '2026-01-14',
          searchTermsTools: 'Google, LinkedIn',
          experienceCredentials: '10 years in marketing',
        },
        bids: {
          bid1Description: 'Acme Corporation - Full service package',
          bid1Total: '50000',
          bid2Description: 'Competitor A - Basic package',
          bid2Total: '45000',
          bid3Description: 'Competitor B - Premium package',
          bid3Total: '60000',
          selectionReason: 'Best value for comprehensive services',
        },
      },
      2: {
        workflow: {
          status: 'COMPLETED',
          submittedDate: '2026-01-10T09:00:00',
          approverApprovalDate: '2026-01-11T10:30:00',
          legalApprovalDate: '2026-01-12T14:00:00',
          apApprovalDate: '2026-01-13T16:00:00',
          requiresLegalReview: false,
        },
        basicDetails: {
          estimatedContractAmount: '75000',
          requestorName: 'DOE, JANE',
          requestorCode: 'A0HZ',
          requestorManager: 'FISCHER, DAVID',
          requestorManagerCode: 'A072',
          companyName: 'Tech Solutions Inc',
          streetAddress: '789 Tech Blvd',
          addressLine2: '',
          zipCodeCity: 'Seattle, WA 98101',
          country: 'United States',
          phone: '555-5678',
          contactPerson: 'Mike Tech',
          contactEmail: 'mike@techsolutions.com',
          howLearnedAbout: 'Industry conference',
          goodsServicesDescription: 'Software development services',
          competitiveBidsObtained: 'Yes',
        },
        thirdPartyRisk: {
          angelRelationships: 'No',
          angelRelationshipsDetails: '',
          personalInfoAccess: 'Yes',
          personalInfoDetails: 'Will have access to user data for analytics',
          specialCircumstances: 'No',
          specialCircumstancesDetails: '',
        },
        approvalDueDiligence: {
          approverName: 'David Fischer',
          legalClearance: 'Yes',
          notes: 'All checks completed successfully',
        },
        commercialDueDiligence: {
          reputationalConcerns: 'No',
          reputationalConcernsDetails: '',
          researchDate: '2026-01-09',
          searchTermsTools: 'Google, Glassdoor, LinkedIn',
          experienceCredentials: '8 years in software development',
        },
        bids: {
          bid1Description: 'Tech Solutions Inc - Full stack development',
          bid1Total: '75000',
          bid2Description: 'DevShop A - Frontend only',
          bid2Total: '50000',
          bid3Description: 'CodeWorks - Full package',
          bid3Total: '85000',
          selectionReason: 'Best technical expertise and reasonable pricing',
        },
      },
      3: {
        workflow: {
          status: 'DRAFT',
          requiresLegalReview: false,
        },
        basicDetails: {
          estimatedContractAmount: '25000',
          requestorName: 'JOHNSON, BOB',
          requestorCode: 'A05N',
          requestorManager: 'REILLY, PATRICK',
          requestorManagerCode: 'A064',
          companyName: 'Global Services LLC',
          streetAddress: '321 Service Road',
          addressLine2: 'Building C',
          zipCodeCity: 'Austin, TX 78701',
          country: 'United States',
          phone: '555-9012',
          contactPerson: 'Sally Services',
          contactEmail: 'sally@globalservices.com',
          howLearnedAbout: 'Web search',
          goodsServicesDescription: 'Consulting services',
          competitiveBidsObtained: 'No',
        },
        thirdPartyRisk: {
          angelRelationships: 'No',
          angelRelationshipsDetails: '',
          personalInfoAccess: 'No',
          personalInfoDetails: '',
          specialCircumstances: 'No',
          specialCircumstancesDetails: '',
        },
        approvalDueDiligence: {
          approverName: '',
          legalClearance: '',
          notes: '',
        },
        commercialDueDiligence: {
          reputationalConcerns: '',
          reputationalConcernsDetails: '',
          researchDate: '',
          searchTermsTools: '',
          experienceCredentials: '',
        },
        bids: {
          bid1Description: '',
          bid1Total: '',
          bid2Description: '',
          bid2Total: '',
          bid3Description: '',
          bid3Total: '',
          selectionReason: 'Still comparing options',
        },
      },
      4: {
        workflow: {
          status: 'PENDING_LEGAL',
          submittedDate: '2026-01-18T14:20:00',
          approverApprovalDate: '2026-01-19T09:15:00',
          requiresLegalReview: true,
        },
        basicDetails: {
          estimatedContractAmount: '100000',
          requestorName: 'NEISH, JEREMY J',
          requestorCode: 'A056',
          requestorManager: 'NAHRWOLD, PAUL',
          requestorManagerCode: 'A0CO',
          companyName: 'Innovative Designs',
          streetAddress: '456 Design Ave',
          addressLine2: '',
          zipCodeCity: 'San Francisco, CA 94102',
          country: 'United States',
          phone: '555-9876',
          contactPerson: 'Tom Designer',
          contactEmail: 'tom@innovativedesigns.com',
          howLearnedAbout: 'Previous collaboration',
          goodsServicesDescription: 'Set design and construction services',
          competitiveBidsObtained: 'Yes',
        },
        thirdPartyRisk: {
          angelRelationships: 'Yes',
          angelRelationshipsDetails: 'Owner is cousin of board member',
          personalInfoAccess: 'No',
          personalInfoDetails: '',
          specialCircumstances: 'No',
          specialCircumstancesDetails: '',
        },
        approvalDueDiligence: {
          approverName: 'Michael Chen',
          legalClearance: 'Pending',
          notes: 'Needs legal review due to relationship',
        },
        commercialDueDiligence: {
          reputationalConcerns: 'No',
          reputationalConcernsDetails: '',
          researchDate: '2026-01-17',
          searchTermsTools: 'Google, Industry databases',
          experienceCredentials: '15 years in set design',
        },
        bids: {
          bid1Description: 'Innovative Designs - Complete package',
          bid1Total: '100000',
          bid2Description: 'Design Studio A',
          bid2Total: '95000',
          bid3Description: 'Build Co',
          bid3Total: '110000',
          selectionReason: 'Best design quality and timeline',
        },
      },
    };
    return mockDataMap[requestId] || null;
  };*/

  const handleNext = async () => {
    // A requestor cannot send their own submission to themselves for approval.
    // Caught here so it surfaces on Basic Details rather than at submit time.
    if (steps[activeStep] === 'Basic Details' && isSelfApprovingManager(formData.basicDetails)) {
      setValidationErrors((prev) => ({
        ...prev,
        basicDetails: [
          ...prev.basicDetails.filter((err) => err.field !== 'requestorManager'),
          { field: 'requestorManager', message: SELF_APPROVAL_MESSAGE },
        ],
      }));
      showNotification(SELF_APPROVAL_MESSAGE, 'error');
      return;
    }

    // Auto-save draft before navigating (silent save, don't navigate away)
    if (formData.workflow.status === 'DRAFT' || formData.workflow.status === 'REJECTED') {
      await handleSaveForm(false);
    }
    setActiveStep((prevStep) => prevStep + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = async () => {
    // Auto-save draft before navigating (silent save, don't navigate away)
    if (formData.workflow.status === 'DRAFT' || formData.workflow.status === 'REJECTED') {
      await handleSaveForm(false);
    }
    setActiveStep((prevStep) => prevStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateFormData = <T extends keyof FormData>(section: T, data: FormData[T]) => {
    setFormData((prev) => ({
      ...prev,
      [section]: data,
    }));
  };

  // Check if Legal review is required based on Third Party Risk answers
  // const checkLegalReviewRequired = () => {
  //   const { angelRelationships, personalInfoAccess, specialCircumstances } = formData.thirdPartyRisk;
  //   // Legal review only required if ANY answer is Yes (skip if ALL are No)
  //   return angelRelationships === 'YES' || personalInfoAccess === 'YES' || specialCircumstances === 'YES';
  // };

  // Map form data to API payload format
  const mapFormDataToAPI = (data: FormData) => {
    return {
      estimated_contract_amount: data.basicDetails.estimatedContractAmount,
      needs_contractor_agreement: data.basicDetails.needsContractorAgreement,
      requestor_name: data.basicDetails.requestorName,
      requestor_code: data.basicDetails.requestorCode,
      requestor_email: data.basicDetails.requestorEmail,
      requestor_manager: data.basicDetails.requestorManager,
      requestor_manager_code: data.basicDetails.requestorManagerCode,
      requestor_manager_email: data.basicDetails.requestorManagerEmail,
      company_name: data.basicDetails.companyName,
      street_address: data.basicDetails.streetAddress,
      address_line_2: data.basicDetails.addressLine2,
      city: data.basicDetails.city,
      zip_code: data.basicDetails.zipCode,
      state: data.basicDetails.state,
      country: data.basicDetails.country,
      phone: data.basicDetails.phone,
      contact_person: data.basicDetails.contactPerson,
      contact_email: data.basicDetails.contactEmail,
      vendor_website: data.basicDetails.vendorWebsite,
      how_learned_about: data.basicDetails.howLearnedAbout,
      goods_services_description: data.basicDetails.goodsServicesDescription,
      ofac_sanctions: data.thirdPartyRisk.ofacSanctions,
      ofac_sanctions_details: data.thirdPartyRisk.ofacSanctionsDetails,
      angel_relationships: data.thirdPartyRisk.angelRelationships,
      angel_relationships_details: data.thirdPartyRisk.angelRelationshipsDetails,
      personal_info_access: data.thirdPartyRisk.personalInfoAccess,
      personal_info_details: data.thirdPartyRisk.personalInfoDetails,
      special_circumstances: data.thirdPartyRisk.specialCircumstances,
      special_circumstances_details: data.thirdPartyRisk.specialCircumstancesDetails,
      payment_terms: data.thirdPartyRisk.paymentTerms || null,
      project_deliverables: data.thirdPartyRisk.projectDeliverables || null,
      free_from_control: data.thirdPartyRisk.freeFromControl || null,
      work_outside_of_normal_business: data.thirdPartyRisk.workOutsideOfNormalBusiness || null,
      independently_established_business: data.thirdPartyRisk.independentlyEstablishedBusiness || null,
      effective_date: data.thirdPartyRisk.effectiveDate || null,
      expiration_date: data.thirdPartyRisk.expirationDate || null,
      approver_name: data.approvalDueDiligence.approverName,
      legal_clearance: data.approvalDueDiligence.legalClearance,
      approval_notes: data.approvalDueDiligence.notes,
      reputational_concerns: data.commercialDueDiligence.reputationalConcerns,
      reputational_concerns_details: data.commercialDueDiligence.reputationalConcernsDetails,
      search_terms_tools: data.commercialDueDiligence.searchTermsTools,
      vendor_added_to_ramp: data.workflow.vendorAddedToRamp || false,
      experience_credentials: data.commercialDueDiligence.experienceCredentials,
      bid_1_description: data.bids.bid1Description,
      bid_1_total: data.bids.bid1Total,
      bid_2_description: data.bids.bid2Description,
      bid_2_total: data.bids.bid2Total,
      bid_3_description: data.bids.bid3Description,
      bid_3_total: data.bids.bid3Total,
      competitive_bids_obtained: data.bids.competitiveBidsObtained,
      selection_reason: data.bids.selectionReason,
    };
  };

  // Save form data (auto-save or manual save)
  const handleSaveForm = async (shouldNavigate: boolean = true): Promise<string | boolean> => {
    try {
      const payload = mapFormDataToAPI(formData);
      
      if (id && id !== 'new') {
        // Update existing form
        await apiClient.put(`/api/vendors/${id}`, payload);
        showNotification('Draft saved successfully', 'success');
        
        // Navigate to dashboard after save (only if explicitly requested)
        if (shouldNavigate) {
          setTimeout(() => navigate('/'), 1500);
        }
        return id;
      } else {
        // Create new form
        const response = await apiClient.post('/api/vendors', payload);
        const newId = response.data.id;
        
        showNotification('Draft saved successfully', 'success');
        
        // Navigate to dashboard after save (only if explicitly requested)
        if (shouldNavigate) {
          setTimeout(() => navigate('/'), 1500);
        }
        return newId;
      }
    } catch (error: any) {
      console.error('Error saving form:', error);
      const errorMsg = error.response?.data?.error || 'Failed to save form';
      const errorDetails = error.response?.data?.details;
      showNotification(
        errorDetails ? `${errorMsg}: ${errorDetails}` : errorMsg,
        'error'
      );
      return false;
    }
  };

  // Handle form submission by requestor
  const handleSubmitForApproval = async () => {
    // Prevent double submission
    if (submitting) return;
    
    setSubmitting(true);
    
    try {
      // Run comprehensive validation
      const errors = validateAllForms(
      formData.basicDetails,
      formData.thirdPartyRisk,
      formData.commercialDueDiligence,
      formData.bids
    );
    
    setValidationErrors(errors);
    
    // Check for W9 file when Ironclad workflow is required
    if (route.requiresIronclad && !formData.workflow.w9FilePath) {
      showNotification('W9 file is required when contractor/consultant agreement is needed', 'error');
      setActiveStep(0); // Navigate to Basic Details where W9 upload is
      setSubmitting(false);
      return;
    }
    
    // Check if there are any validation errors
    if (hasValidationErrors(errors)) {
        const summary = getValidationSummary(errors);
        showNotification(`Please fix validation errors: ${summary}`, 'error');
        
        // Navigate to the first form with errors. Resolve the target by step
        // name rather than a fixed index, because the visible step list varies
        // by workflow and hardcoded indices land on the wrong step (or out of
        // bounds) in the shorter flows.
        const goToStepNamed = (name: string) => {
          const idx = steps.indexOf(name);
          if (idx >= 0) setActiveStep(idx);
        };
        if (errors.basicDetails.length > 0) {
          goToStepNamed('Basic Details');
        } else if (errors.thirdPartyRisk.length > 0) {
          goToStepNamed('Risk Profile');
        } else if (errors.commercialDueDiligence.length > 0) {
          goToStepNamed('Due Diligence');
        } else if (errors.bids.length > 0) {
          goToStepNamed('Competitive Bidding');
        }
        
        setSubmitting(false);
        return;
    }
    
    // Clear validation errors if all passed
    setValidationErrors({
      basicDetails: [],
      thirdPartyRisk: [],
      commercialDueDiligence: [],
      bids: [],
    });
    
      // Save first to get an ID if it's a new form
      const savedId = await handleSaveForm();
      if (!savedId) {
        showNotification('Failed to save form', 'error');
        setSubmitting(false);
        return;
      }
      
      const formId = savedId === true ? id : savedId;
      
      await apiClient.post(`/api/vendors/${formId}/submit`);
      showNotification('Form submitted for approval', 'success');
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      console.error('Error submitting form:', error);
      showNotification('Error submitting form', 'error');
      setSubmitting(false);
    }
  };

  // Open approval dialog
  const openApprovalDialog = (stage: 'manager' | 'legal' | 'ap') => {
    setApprovalStage(stage);
    setApprovalComment('');
    setApprovalDialogOpen(true);
  };

  // Handle manager approval
  const handleManagerApproval = async () => {
    try {
      const response = await apiClient.post(`/api/vendors/${id}/approve`, { 
        stage: 'manager',
        approval_comment: approvalComment || undefined 
      });
      const result = response.data;
      setFormData((prev) => ({
        ...prev,
        workflow: {
          ...prev.workflow,
          status: result.newStatus,
          approverApprovalDate: new Date().toISOString(),
        },
      }));
      const requiresLegal = formData.workflow.requiresLegalReview;
      const ironcladLaunched = result.ironcladLaunched;
      
      let message = `Approved by manager. ${requiresLegal ? 'Routing to Legal.' : 'Routing to AP.'}`;
      if (ironcladLaunched) {
        message += ' Ironclad contract workflow has been initiated.';
      }
      
      showNotification(message, 'success');
      setApprovalDialogOpen(false);
      setApprovalComment('');
      setTimeout(() => navigate('/'), 1500);
    } catch (error: any) {
      console.error('Error approving:', error);
      const errorMsg = error.response?.data?.error || 'Failed to approve vendor submission';
      const errorDetails = error.response?.data?.details;
      showNotification(
        errorDetails ? `${errorMsg}: ${errorDetails}` : errorMsg,
        'error'
      );
    }
  };

  // Handle legal approval
  const handleLegalApproval = async () => {
    try {
      await apiClient.post(`/api/vendors/${id}/approve`, { 
        stage: 'legal',
        approval_comment: approvalComment || undefined 
      });
      setFormData((prev) => ({
        ...prev,
        workflow: {
          ...prev.workflow,
          status: 'PENDING_AP',
          legalApprovalDate: new Date().toISOString(),
        },
      }));
      showNotification('Approved by Legal. Routing to AP.', 'success');
      setApprovalDialogOpen(false);
      setApprovalComment('');
      setTimeout(() => navigate('/'), 1500);
    } catch (error: any) {
      console.error('Error approving:', error);
      showNotification(
        error.response?.data?.error || 'Error approving submission',
        'error'
      );
    }
  };

  // Handle AP completion
  const handleAPCompletion = async () => {
    try {
      await apiClient.post(`/api/vendors/${id}/approve`, { 
        stage: 'ap',
        approval_comment: approvalComment || undefined 
      });
      setFormData((prev) => ({
        ...prev,
        workflow: {
          ...prev.workflow,
          status: 'COMPLETED',
          apApprovalDate: new Date().toISOString(),
        },
      }));
      showNotification('Vendor approved and marked complete.', 'success');
      setApprovalDialogOpen(false);
      setApprovalComment('');
      setTimeout(() => navigate('/'), 1500);
    } catch (error: any) {
      console.error('Error completing:', error);
      showNotification(
        error.response?.data?.error || 'Error completing submission',
        'error'
      );
    }
  };

  // Handle manual notification resend
  const handleResendNotification = async () => {
    try {
      const response = await apiClient.post(`/api/vendors/${id}/resend-notification`);
      const result = response.data;
      if (result.success) {
        showNotification('Notification sent successfully!', 'success');
      } else {
        showNotification(result.error || 'Failed to send notifications', 'error');
      }
    } catch (error: any) {
      console.error('Error resending notifications:', error);
      showNotification(
        error.response?.data?.error || 'Error sending notifications',
        'error'
      );
    }
  };

  // Handle rejection
  const handleRejection = () => {
    setRejectionDialogOpen(true);
  };

  const handleReject = async () => {
    // Validate rejection reason is not empty
    if (!rejectionReason || rejectionReason.trim().length === 0) {
      showNotification('Rejection reason is required. Please provide a detailed explanation.', 'error');
      return;
    }
    
    // Ensure minimum length for meaningful rejection reason
    if (rejectionReason.trim().length < 10) {
      showNotification('Please provide a more detailed rejection reason (at least 10 characters).', 'error');
      return;
    }
    
    try {
      await apiClient.post(`/api/vendors/${id}/reject`, { reason: rejectionReason });
      setFormData((prev) => ({
        ...prev,
        workflow: {
          ...prev.workflow,
          status: 'REJECTED',
          rejectionReason: rejectionReason,
        },
      }));
      showNotification('Form rejected', 'success');
      setRejectionDialogOpen(false);
      setRejectionReason('');
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      console.error('Error rejecting form:', error);
      showNotification('Error rejecting form', 'error');
    }
  };

  const handleRejectCancel = () => {
    setRejectionDialogOpen(false);
    setRejectionReason('');
  };

  // Get workflow status display
  const getWorkflowStatusChip = () => {
    const { status } = formData.workflow;
    const statusConfig: Record<WorkflowStatus, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
      DRAFT: { label: 'Draft', color: 'default' },
      PENDING_MANAGER_APPROVAL: { label: 'Pending Manager Approval', color: 'warning' },
      PENDING_LEGAL: { label: 'Pending Legal Review', color: 'info' },
      PENDING_AP: { label: 'Pending AP Approval', color: 'primary' },
      COMPLETED: { label: 'Completed', color: 'success' },
      REJECTED: { label: 'Rejected', color: 'error' },
    };
    const config = statusConfig[status];
    return <Chip label={config.label} color={config.color} icon={status === 'COMPLETED' ? <CheckCircle /> : <Pending />} />;
  };

  const renderStepContent = (step: number) => {
    const currentStepName = steps[step];
    
    switch (currentStepName) {
      case 'Basic Details':
        return (
          <BasicDetailsForm 
            data={formData.basicDetails} 
            onChange={(data) => updateFormData('basicDetails', data)}
            disabled={!canEditForm()}
            errors={validationErrors.basicDetails}
            w9FilePath={formData.workflow.w9FilePath}
            w9FileName={formData.workflow.w9FileName}
            formId={id}
            requiresIronclad={route.requiresIronclad}
            onW9Upload={(path, filename) => {
              setFormData({
                ...formData,
                workflow: {
                  ...formData.workflow,
                  w9FilePath: path,
                  w9FileName: filename,
                },
              });
            }}
          />
        );
      case 'Risk Profile':
        return (
          <ThirdPartyRiskForm 
            data={formData.thirdPartyRisk} 
            onChange={(data) => updateFormData('thirdPartyRisk', data)}
            disabled={!canEditForm()}
            errors={validationErrors.thirdPartyRisk}
            needsIronclad={route.requiresIronclad}
            showFamilial={parseFloat(formData.basicDetails.estimatedContractAmount) >= 1000}
          />
        );
      case 'Due Diligence':
        return (
          <CommercialDueDiligenceForm 
            data={formData.commercialDueDiligence} 
            onChange={(data) => updateFormData('commercialDueDiligence', data)}
            disabled={!canEditForm()}
            estimatedContractAmount={formData.basicDetails.estimatedContractAmount}
            errors={validationErrors.commercialDueDiligence}
          />
        );
      case 'Competitive Bidding':
        return (
          <BidsForm 
            data={formData.bids} 
            onChange={(data) => updateFormData('bids', data)}
            disabled={!canEditForm()}
            errors={validationErrors.bids}
          />
        );
      case 'Review':
        return <SupplierReviewForm formData={formData} formId={id} />;
      default:
        return null;
    }
  };

  // Determine if user is the original requestor
  // Primary check: identity.id matches created_by_user_id (most reliable)
  // Fallback: case-insensitive email comparison
  const isRequestor = (): boolean => {
    // Primary: auth system ID match
    if (identity?.id && formData.workflow.createdByUserId &&
        identity.id === formData.workflow.createdByUserId) {
      return true;
    }
    // Fallback: email match (case-insensitive)
    if (user?.email && formData.basicDetails.requestorEmail &&
        user.email.toLowerCase() === formData.basicDetails.requestorEmail.toLowerCase()) {
      return true;
    }
    return false;
  };

  // Determine if user can edit the form
  // Can edit if: status is DRAFT or REJECTED AND (user is requestor OR user is admin/super_admin)
  const canEditForm = (): boolean => {
    const isDraftOrRejected = formData.workflow.status === 'DRAFT' || formData.workflow.status === 'REJECTED';
    const requestor = isRequestor();
    const isAdminUser = isAdmin || isSuperAdmin;
    
    const result = isDraftOrRejected && (requestor || isAdminUser);
    
    console.log('[Permissions] canEditForm:', result, {
      status: formData.workflow.status,
      isDraftOrRejected,
      identityId: identity?.id,
      createdByUserId: formData.workflow.createdByUserId,
      userEmail: user?.email,
      requestorEmail: formData.basicDetails.requestorEmail,
      isRequestor: requestor,
      isAdmin,
      isSuperAdmin,
      isAdminUser,
    });
    
    return result;
  };
  
  // Determine if user can submit (only original requestor, not admins)
  const canSubmitForm = (): boolean => {
    const isDraftOrRejected = formData.workflow.status === 'DRAFT' || formData.workflow.status === 'REJECTED';
    return isDraftOrRejected && isRequestor();
  };

  const progress = steps.length > 1 ? (activeStep / (steps.length - 1)) * 100 : 0;
  const nextStepName = activeStep < steps.length - 1 ? steps[activeStep + 1] : null;

  // Determine if form is in read-only mode (COMPLETE status disables all fields)
  // const isReadOnly = formData.workflow.status === 'COMPLETE';

  // Determine current user role (this would come from authentication in real implementation)
  // const getCurrentUserRole = (): 'requestor' | 'approver' | 'legal' | 'ap' => {
  //   // TODO: Get from authentication context
  //   return 'requestor'; // Default for now
  // };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Typography variant="h6" color="text.secondary">Loading...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      {/* Back to Dashboard Button */}
      <Box sx={{ mb: 1 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          variant="text"
        >
          Back to Dashboard
        </Button>
      </Box>

      {/* Important Notice */}
      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>This form does NOT approve spending!</strong> It only approves adding the vendor to the financial system and must be completed before any spending can be approved.
      </Alert>

      {/* Rejection Banner - Sticky at top */}
      {formData.workflow.status === 'REJECTED' && formData.workflow.rejectionReason && (
        <RejectionBanner rejectionReason={formData.workflow.rejectionReason} />
      )}

      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4, md: 5 }, mt: 2, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 4 }}>
          {formData.basicDetails.companyName || 'New Vendor'} Due Diligence
        </Typography>
        
        {/* Workflow Status */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getWorkflowStatusChip()}
            {formData.workflow.requiresLegalReview && formData.workflow.status !== 'DRAFT' && (
              <Chip label="Legal Review Required" color="info" size="small" />
            )}
          </Box>
          {(formData.workflow.status === 'PENDING_MANAGER_APPROVAL' || 
            formData.workflow.status === 'PENDING_LEGAL' || 
            formData.workflow.status === 'PENDING_AP') && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<Notifications />}
              onClick={handleResendNotification}
            >
              Resend Notification
            </Button>
          )}
        </Box>

        {/* Success Message Only */}
        {formData.workflow.status === 'COMPLETED' && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <strong>Process Complete!</strong> Vendor has been created in NetSuite.
          </Alert>
        )}

        {/* Validation Errors Summary */}
        {hasValidationErrors(validationErrors) && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <strong>Please fix the following issues:</strong>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {validationErrors.basicDetails.map((err, idx) => (
                <li key={`basic-${idx}`}>{err.message}</li>
              ))}
              {validationErrors.thirdPartyRisk.map((err, idx) => (
                <li key={`risk-${idx}`}>{err.message}</li>
              ))}
              {validationErrors.commercialDueDiligence.map((err, idx) => (
                <li key={`commercial-${idx}`}>{err.message}</li>
              ))}
              {validationErrors.bids.map((err, idx) => (
                <li key={`bids-${idx}`}>{err.message}</li>
              ))}
            </Box>
          </Alert>
        )}

        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" color="primary">
              {steps[activeStep]}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
              {Math.round(progress)}% Complete
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
              }
            }} 
          />
        </Box>

        {/* Top Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<ChevronLeft />}
            size="large"
          >
            Back
          </Button>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {canEditForm() && (
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={() => handleSaveForm()}
              >
                Save Draft
              </Button>
            )}
            {activeStep === steps.length - 1 && canSubmitForm() ? (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSubmitForApproval}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit for Approval'}
              </Button>
            ) : activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ChevronRight />}
                size="large"
              >
                Next
              </Button>
            ) : null}
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          {renderStepContent(activeStep)}
        </Box>

        {/* Approval Action Buttons */}
        {formData.workflow.status === 'PENDING_MANAGER_APPROVAL' && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={() => openApprovalDialog('manager')}
            >
              Approve (Manager)
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="large"
              onClick={handleRejection}
            >
              Reject
            </Button>
          </Box>
        )}
        {formData.workflow.status === 'PENDING_LEGAL' && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={() => openApprovalDialog('legal')}
            >
              Approve (Legal)
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="large"
              onClick={handleRejection}
            >
              Reject
            </Button>
          </Box>
        )}
        {formData.workflow.status === 'PENDING_AP' && (
          <>
            {(user?.subDepartmentDesc?.toLowerCase().includes('accounts payable') || isSuperAdmin) && (
              <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.workflow.vendorAddedToRamp || false}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          workflow: {
                            ...formData.workflow,
                            vendorAddedToRamp: e.target.checked,
                          },
                        });
                      }}
                      color="primary"
                    />
                  }
                  label="Vendor Added to Ramp"
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="success"
                size="large"
                onClick={() => openApprovalDialog('ap')}
              >
                Mark Complete (AP)
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="large"
                onClick={handleRejection}
              >
                Reject
              </Button>
            </Box>
          </>
        )}

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<ChevronLeft />}
              size="large"
            >
              Back
            </Button>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
              {canEditForm() && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => handleSaveForm()}
                >
                  Save Draft
                </Button>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                {nextStepName && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                    Next: {nextStepName}
                  </Typography>
                )}
                {activeStep === steps.length - 1 && canSubmitForm() ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSubmitForApproval}
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit for Approval'}
                  </Button>
                ) : activeStep < steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ChevronRight />}
                    size="large"
                  >
                    Next
                  </Button>
                ) : null}
              </Box>
            </Box>
        </Box>
      </Paper>

      {/* Rejection Dialog */}
      <Dialog open={rejectionDialogOpen} onClose={handleRejectCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this vendor due diligence request:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Explain why this request is being rejected..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleReject} color="error" variant="contained">
            Yes, Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onClose={() => setApprovalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {approvalStage === 'manager' && 'Manager Approval'}
          {approvalStage === 'legal' && 'Legal Approval'}
          {approvalStage === 'ap' && 'AP Completion'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {approvalStage === 'manager' && 'Approve this vendor request as the manager?'}
            {approvalStage === 'legal' && 'Approve this vendor request for legal clearance?'}
            {approvalStage === 'ap' && 'Mark this vendor request as complete?'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label="Approval Comment (Optional)"
            value={approvalComment}
            onChange={(e) => setApprovalComment(e.target.value)}
            placeholder="Add any comments or notes about this approval..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={approvalStage === 'manager' ? handleManagerApproval : approvalStage === 'legal' ? handleLegalApproval : handleAPCompletion} 
            color="success" 
            variant="contained"
          >
            Confirm Approval
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MultiStepForm;
